/**
 * Parsing helpers for the video urls editors paste into Crystallize. They stay free of any
 * rendering concern so both the tiles and the schema.org builder can share them.
 */

/** Relative urls have to be resolvable too, so parsing always runs against a base. */
const PARSE_BASE = 'https://video-url-helper.invalid';

const DIRECT_MEDIA_EXTENSIONS = ['.mp4', '.webm', '.m3u8', '.mov'];

/**
 * Only a web url may reach a media element or the structured data, however plausible the path
 * of a `javascript:` or `data:` value looks.
 */
const DIRECT_MEDIA_PROTOCOLS = new Set(['http:', 'https:']);

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

const YOUTUBE_SHORT_HOSTS = new Set(['youtu.be', 'www.youtu.be']);

const YOUTUBE_PATH_PREFIXES = ['/embed/', '/shorts/', '/v/'];

const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

const VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const NUMERIC_ID_PATTERN = /^\d+$/;

function parseUrl(url: string | null | undefined): URL | undefined {
  if (!url) {
    return undefined;
  }

  try {
    return new URL(url.trim(), PARSE_BASE);
  } catch {
    return undefined;
  }
}

/** Keeps the first path segment only, so trailing segments or slashes never leak into an id. */
function readVideoId(rawId: string): string | undefined {
  const [id] = rawId.split('/');

  if (!id || !VIDEO_ID_PATTERN.test(id)) {
    return undefined;
  }

  return id;
}

function readYouTubePathId(pathname: string): string | undefined {
  const prefix = YOUTUBE_PATH_PREFIXES.find((candidate) => pathname.startsWith(candidate));

  if (!prefix) {
    return undefined;
  }

  return readVideoId(pathname.slice(prefix.length));
}

/**
 * Supports the four link shapes editors copy from YouTube: `watch?v=`, `youtu.be/`,
 * `/embed/` and `/shorts/`.
 */
export function getYouTubeVideoId(url: string | null | undefined): string | undefined {
  const parsed = parseUrl(url);

  if (!parsed) {
    return undefined;
  }

  const host = parsed.hostname.toLowerCase();

  if (YOUTUBE_SHORT_HOSTS.has(host)) {
    return readVideoId(parsed.pathname.slice(1));
  }

  if (!YOUTUBE_HOSTS.has(host)) {
    return undefined;
  }

  const watchId = parsed.searchParams.get('v');

  if (watchId) {
    return readVideoId(watchId);
  }

  return readYouTubePathId(parsed.pathname);
}

/** Supports both `vimeo.com/<id>` and the player url `player.vimeo.com/video/<id>`. */
export function getVimeoVideoId(url: string | null | undefined): string | undefined {
  const parsed = parseUrl(url);

  if (!parsed || !VIMEO_HOSTS.has(parsed.hostname.toLowerCase())) {
    return undefined;
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  const candidate = segments[0] === 'video' ? segments[1] : segments[0];

  if (!candidate || !NUMERIC_ID_PATTERN.test(candidate)) {
    return undefined;
  }

  return candidate;
}

/**
 * True for a url a `<video>` element can play by itself. An embed (YouTube, Vimeo) cannot be
 * autoplayed muted behind hero content, so callers use this to fall back to the poster.
 */
export function isDirectMediaUrl(url: string | null | undefined): boolean {
  const parsed = parseUrl(url);

  if (!parsed || !DIRECT_MEDIA_PROTOCOLS.has(parsed.protocol)) {
    return false;
  }

  const pathname = parsed.pathname.toLowerCase();

  return DIRECT_MEDIA_EXTENSIONS.some((extension) => pathname.endsWith(extension));
}
